import { useEffect, useState } from "react";
import ImageCarousel from "@/components/base/ImageCarousel";
import PriceDisplay from "@/components/base/PriceDisplay";

interface PurchaseOption {
  id: string;
  icon: string;
  label: string;
  subLabel: string;
  url: string;
  colorClass: string;
}

interface ProductDetailItem {
  id: string;
  title: string;
  description: string;
  long_description?: string | null;
  highlights?: string[] | null;
  price: string;
  discounted_price?: string | null;
  image: string;
  images?: string[];
  badge?: string | null;
  category?: string;
  purchaseOptions: PurchaseOption[];
}

interface ProductDetailModalProps {
  product: ProductDetailItem | null;
  onClose: () => void;
}

const ProductDetailModal = ({ product, onClose }: ProductDetailModalProps) => {
  const [activeTab, setActiveTab] = useState<"overview" | "buy">("overview");
  const [activeImageIdx, setActiveImageIdx] = useState(0);

  useEffect(() => {
    if (product) {
      document.body.style.overflow = "hidden";
      setActiveTab("overview");
      setActiveImageIdx(0);
    }
    return () => { document.body.style.overflow = ""; };
  }, [product]);

  if (!product) return null;

  const displayImages =
    product.images && product.images.length > 0
      ? product.images
      : product.image
      ? [product.image]
      : [];

  const highlights = product.highlights?.filter(Boolean) || [];
  const hasDetails = product.long_description || highlights.length > 0;
  const hasOptions = product.purchaseOptions && product.purchaseOptions.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white rounded-3xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="h-1 w-full bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] flex-shrink-0" />

        {/* Close btn */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 hover:bg-gray-100 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-gray-600 text-lg" />
        </button>

        <div className="flex-1 overflow-y-auto">
          {/* Top: Image + Basic Info */}
          <div className="flex flex-col lg:flex-row gap-0">
            {/* Image Gallery */}
            <div className="lg:w-[45%] flex-shrink-0">
              <div className="relative h-72 lg:h-full min-h-[280px]">
                <ImageCarousel
                  images={displayImages}
                  alt={product.title}
                  className="w-full h-full"
                  showArrows
                  showDots
                />
                {product.badge && (
                  <span className="absolute top-4 left-4 z-10 px-3 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a]">
                    {product.badge}
                  </span>
                )}
              </div>
              {/* Thumbnail row */}
              {displayImages.length > 1 && (
                <div className="flex gap-2 p-3 overflow-x-auto bg-gray-50">
                  {displayImages.map((img, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImageIdx(i)}
                      className={`w-14 h-14 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                        activeImageIdx === i ? "border-[#3a8cbf]" : "border-transparent hover:border-gray-300"
                      }`}
                    >
                      <img src={img} alt={`View ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Info Panel */}
            <div className="flex-1 flex flex-col p-6 lg:p-7">
              {/* Category pill */}
              {product.category && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-[#3a8cbf] bg-[#3a8cbf]/8 px-2.5 py-1 rounded-full w-fit mb-3 capitalize">
                  <i className="ri-folder-line text-xs" />
                  {product.category.replace(/-/g, " ")}
                </span>
              )}

              <h2
                className="text-2xl font-bold text-gray-900 leading-tight mb-2"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {product.title}
              </h2>

              <p className="text-sm text-gray-500 leading-relaxed mb-4">
                {product.description}
              </p>

              <div className="mb-5">
                <PriceDisplay price={product.price} discountedPrice={product.discounted_price} size="lg" />
              </div>

              {/* Tabs */}
              <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-5">
                <button
                  onClick={() => setActiveTab("overview")}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === "overview" ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <i className="ri-information-line mr-1.5" />
                  Details
                </button>
                <button
                  onClick={() => setActiveTab("buy")}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeTab === "buy" ? "bg-white text-gray-900" : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <i className="ri-shopping-bag-line mr-1.5" />
                  Buy Now
                  {hasOptions && (
                    <span className="ml-1.5 text-xs bg-[#e05a8a]/15 text-[#e05a8a] px-1.5 py-0.5 rounded-full font-semibold">
                      {product.purchaseOptions.length}
                    </span>
                  )}
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === "overview" && (
                <div className="flex-1 flex flex-col gap-4">
                  {product.long_description && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-2">About This Product</h4>
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                        {product.long_description}
                      </p>
                    </div>
                  )}

                  {highlights.length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">Highlights</h4>
                      <ul className="flex flex-col gap-2">
                        {highlights.map((h, i) => (
                          <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                            <span className="w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-br from-[#3a8cbf] to-[#e05a8a] flex-shrink-0 mt-0.5">
                              <i className="ri-check-line text-white text-xs" />
                            </span>
                            {h}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {!hasDetails && (
                    <div className="flex flex-col items-center justify-center py-6 text-center text-gray-400">
                      <i className="ri-information-line text-3xl mb-2 opacity-40" />
                      <p className="text-sm">No additional details added yet.</p>
                    </div>
                  )}

                  {/* CTA to switch to Buy tab */}
                  <button
                    onClick={() => setActiveTab("buy")}
                    className="mt-auto w-full py-3.5 rounded-xl bg-gradient-to-r from-[#3a8cbf] to-[#e05a8a] text-white font-semibold text-sm hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-shopping-bag-line" />
                    View Purchase Options
                  </button>
                </div>
              )}

              {activeTab === "buy" && (
                <div className="flex-1 flex flex-col gap-3">
                  {hasOptions ? (
                    <>
                      <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-1">
                        Choose where to buy
                      </p>
                      {product.purchaseOptions.map((opt) => (
                        <a
                          key={opt.id}
                          href={opt.url}
                          target="_blank"
                          rel="noopener noreferrer nofollow"
                          className="group flex items-center gap-4 p-4 rounded-2xl border border-gray-100 hover:border-transparent hover:bg-gradient-to-r hover:from-[#5FA8D3]/5 hover:to-[#F78FB3]/5 transition-all duration-200 cursor-pointer"
                        >
                          <div className={`w-11 h-11 flex items-center justify-center rounded-xl bg-gradient-to-br ${opt.colorClass} flex-shrink-0`}>
                            <i className={`${opt.icon} text-white text-lg`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-900 text-sm">{opt.label}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{opt.subLabel}</p>
                          </div>
                          <div className="w-8 h-8 flex items-center justify-center rounded-full border border-gray-200 group-hover:border-transparent group-hover:bg-gradient-to-r group-hover:from-[#5FA8D3] group-hover:to-[#F78FB3] transition-all duration-200 flex-shrink-0">
                            <i className="ri-arrow-right-line text-gray-400 group-hover:text-white text-sm transition-colors" />
                          </div>
                        </a>
                      ))}
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-center text-gray-400">
                      <i className="ri-shopping-cart-line text-3xl mb-2 opacity-40" />
                      <p className="text-sm">No purchase options available yet.</p>
                    </div>
                  )}

                  <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-center gap-2 text-xs text-gray-400">
                    <i className="ri-shield-check-line text-green-500" />
                    <span>Secure checkout on all platforms</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailModal;
